// debugLogger.js — Captures console, errors, and network failures.
// Buffers in memory, flushes batches to /api/logs every 3s.
//
// FROZEN — do not modify. The /api/logs exclusion guard is intentional.
//
// Usage:
//   import DebugLogger from './debugLogger';
//   DebugLogger.setToken(token);   // call after login
//   DebugLogger.getLogs();         // in-memory array
//   DebugLogger.clear();

const DebugLogger = (() => {
  const LOG_LIMIT = 500;
  let _logs = [], _token = null, _flushTimer = null, _queue = [];

  const push = (level, args, stack) => {
    const entry = {
      id: Date.now() + Math.random(), level,
      message: args.map(a => { try { return typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a); } catch { return String(a); } }).join(' '),
      stack: stack || null, url: window.location.href, time: new Date().toISOString(),
    };
    _logs.unshift(entry);
    if (_logs.length > LOG_LIMIT) _logs.pop();
    _queue.push(entry);
    scheduleFlush();
    return entry;
  };

  const scheduleFlush = () => { if (_flushTimer) return; _flushTimer = setTimeout(flush, 3000); };

  const flush = async () => {
    _flushTimer = null;
    if (!_queue.length || !_token) return;
    const batch = _queue.splice(0);
    for (const e of batch) {
      try {
        await fetch('/api/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${_token}` },
          body: JSON.stringify({ level: e.level, message: e.message, stack: e.stack, url: e.url }),
        });
      } catch {}
    }
  };

  // Intercept console
  const _orig = {};
  ['log','info','warn','error','debug'].forEach(m => {
    _orig[m] = console[m].bind(console);
    console[m] = (...args) => { _orig[m](...args); push(m, args); };
  });

  // Uncaught errors
  window.addEventListener('error', e => push('error', [e.message || 'Uncaught error'], e.error?.stack));
  window.addEventListener('unhandledrejection', e => push('error', ['Unhandled Promise: ' + (e.reason?.message || e.reason)], e.reason?.stack));

  // Network — exclude /api/logs to prevent infinite loop
  const _origFetch = window.fetch;
  window.fetch = async (...args) => {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '?';
    const method = args[1]?.method || 'GET';
    try {
      const res = await _origFetch(...args);
      if (!res.ok && !url.includes('/api/logs')) push('network', [`${method} ${url} → ${res.status} ${res.statusText}`]);
      return res;
    } catch (err) {
      push('network', [`${method} ${url} → FAILED: ${err.message}`], err.stack);
      throw err;
    }
  };

  return {
    setToken: t => { _token = t; scheduleFlush(); },
    getLogs: () => _logs,
    clear: () => { _logs = []; _queue = []; },
    flush, push,
  };
})();

export default DebugLogger;
